import { animate, state, style, transition, trigger } from "@angular/animations";
import { DatePipe } from "@angular/common";
import { AfterViewInit, Component, Input, OnInit, ViewChild, inject, output, computed } from "@angular/core";
import { LangChangeEvent, TranslateModule, TranslateService } from "@ngx-translate/core";
import { DragResizeService, WindowComponent, WindowTitleBarComponent } from "@progress/kendo-angular-dialog";
import { GridComponent, GridDataResult, GridModule, PageChangeEvent, PagerPosition, PagerType } from "@progress/kendo-angular-grid";
import { TooltipDirective } from "@progress/kendo-angular-tooltip";
import { CompositeFilterDescriptor, filterBy } from "@progress/kendo-data-query";
import * as Sentry from "@sentry/browser";
import { AuditTrailEvent } from "../settings/audit-trail-service/audit-trail-event.interface";
import { AuditTrailService } from "../settings/audit-trail-service/audit-trail.service";
import { AuditTrailEventViewModel } from "../settings/audit-trail/audit-trail-event-viewmodel.interface";
import {
    AuthService,
    CategoriesService,
    DashboardsService,
    DeliveryMethodsService,
    IFirmDeliveryMethodsService,
    FirmsService,
    HelpersService,
    IFirmGroupsService,
    IFirmRolesService,
    MappingOfStatusesService,
    UsersService,
    WkTranslatePipe,
    isUnassigned,
} from "@shared/index";
import { ApplicationsService } from "../shared/applications-service/applications.service";
import { IFirmUsersService } from "../shared/ifirm-users-service/ifirm-users.service";
import { ICategories } from "../views-interfaces/categories.interface";
import { IDashboardModel, isiFirmTaxDashboard } from "../views-interfaces/dashboard-model.interface";
import { TranslateObject } from "../views-interfaces/translate-object.interface";
import { UserDashboardSettingsService } from "@shared/user-dashboard-settings-service/user-dashboard-settings.service";
import { CardDisplayService } from "../shared/card-display/card-display.service";
import { DatePickerModule } from "@progress/kendo-angular-dateinputs";
import { SharedModule } from "@app/shared/shared.module";
import { TextBoxModule } from "@progress/kendo-angular-inputs";
import { FormsModule } from "@angular/forms";
import { JobsAndBillingJobTypesService } from "@shared/jobs-and-billing-job-types-service";
import { JobsAndBillingPriorityService } from "@shared/jobs-and-billing-priority";
import { JobsAndBillingStatusesService } from "@shared/jobs-and-billing-statuses-service";
import { JobsAndBillingCommonService } from "@shared/jobs-and-billing-common-service";

interface PathObject {
    op: string;
    path: string;
    value?: any;
}

interface StepInfo {
    stepPath: string;
    stepValue: string;
}

@Component({
    selector: "wk-card-audit-trail",
    templateUrl: "./card-audit-trail.component.html",
    styleUrl: "./card-audit-trail.component.scss",
    host: { class: "wk-cat" },
    imports: [
        WindowComponent,
        TooltipDirective,
        TranslateModule,
        WindowTitleBarComponent,
        DatePickerModule,
        GridComponent,
        SharedModule,
        TextBoxModule,
        GridModule,
        FormsModule,
    ],
    animations: [
        trigger("overlayAppear", [
            state("in", style({ opacity: 1 })),
            transition("void => *", [style({ opacity: 0.1 }), animate(".3s cubic-bezier(.2, .6, .4, 1)")]),
        ]),
        trigger("dialogSlideInAppear", [
            state("in", style({ transform: "translate(0, 0)" })),
            transition("void => *", [style({ transform: "translate(0, -10%)" }), animate(".3s cubic-bezier(.2, 1, .2, 1)")]),
        ]),
    ],
    providers: [WkTranslatePipe],
})
export class CardAuditTrailComponent implements AfterViewInit, OnInit {
    private readonly auditTrailService = inject(AuditTrailService);
    private readonly authService = inject(AuthService);
    private readonly categoriesService = inject(CategoriesService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly datePipe = inject(DatePipe);
    private readonly deliveryMethodsService = inject(DeliveryMethodsService);
    private readonly iFirmDeliveryMethodsService = inject(IFirmDeliveryMethodsService);
    private readonly firmsService = inject(FirmsService);
    private readonly helpersService = inject(HelpersService);
    private readonly iFirmGroupsService = inject(IFirmGroupsService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly translateService = inject(TranslateService);
    private readonly usersService = inject(UsersService);
    private readonly iFirmUsersService = inject(IFirmUsersService);
    private readonly applicationsService = inject(ApplicationsService);
    private readonly userDashboardSettingsService = inject(UserDashboardSettingsService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly jobsAndBillingJobTypesService = inject(JobsAndBillingJobTypesService);
    private readonly wkTranslatePipe = inject(WkTranslatePipe);
    private readonly jobsAndBillingPriorityService = inject(JobsAndBillingPriorityService);
    private readonly jobsAndBillingStatusesService = inject(JobsAndBillingStatusesService);
    protected readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);

    @Input() cardId: string;
    @Input() dialogWidth: number;
    @Input() dialogHeight: number;
    @Input() dialogMinWidth: number;
    @Input() dialogMinHeight: number;
    readonly close = output<any>();
    @ViewChild(WindowComponent, { static: false }) windowComponent: WindowComponent;
    @ViewChild(TooltipDirective) tooltipDir: TooltipDirective;
    currentLanguage = "en-CA";
    dashboards: any;
    currentDashboard: IDashboardModel;
    categories: ICategories[];
    gridData: AuditTrailEventViewModel[];
    gridMainData: AuditTrailEventViewModel[];
    auditsData: any;
    isLoading = true;
    pageSize = 10;
    pagerButtonCount = 0;
    pagerInfo = false;
    skip = 0;
    deliveryMethods;
    gridView: GridDataResult;
    isMaximized = false;
    public type: PagerType = "numeric";
    public pageSizes = false;
    public previousNext = false;
    public position: PagerPosition = "bottom";
    public Actionfilter: CompositeFilterDescriptor = { logic: "and", filters: [] };
    public FromDatefilter: CompositeFilterDescriptor = { logic: "and", filters: [] };
    public ToDatefilter: CompositeFilterDescriptor = { logic: "and", filters: [] };
    public Userfilter: CompositeFilterDescriptor = { logic: "and", filters: [] };
    public userIDData = {
        Option: [],
        selectedValues: [],
    };
    public actionData = {
        Option: [],
        selectedValues: [],
    };

    constructor() {
        this.currentLanguage = this.translateService.getCurrentLang()
            ? this.translateService.getCurrentLang()
            : this.translateService.getFallbackLang();
    }

    async ngOnInit() {
        // Get all Dashboards for populating component dropdown
        await this.helpersService.mergeUserData();
        this.dashboards = await this.applicationsService.getAccessibleDashboards();
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        if (isiFirmTaxDashboard(this.currentDashboard) && this.currentDashboard.TaxYear >= 2025) {
            this.deliveryMethods = (await this.iFirmDeliveryMethodsService.getiFirmDeliveryMethods()).reduce((deliveryMethods, method) => {
                deliveryMethods[method.Id] = method as TranslateObject;
                return deliveryMethods;
            }, {});
        } else {
            this.deliveryMethods = this.deliveryMethodsService.getDeliveryMethodsList(this.currentDashboard);
        }

        this.categories = await this.categoriesService.getCategoriesList();
        this.refreshData();

        // Get current application language
        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.currentLanguage = event.lang;
            if (this.auditsData) {
                this.gridMainData = this.updateViewModel(this.auditsData);
                this.gridData = this.gridMainData;
                this.loadItems();
            }
        });
    }

    ngAfterViewInit() {
        this.centerWindow();
    }

    refreshData() {
        Sentry.startSpanManual(
            {
                name: "WebApp_Performance-CardAuditTrail",
                op: "CardAuditTrail",
            },
            async (span) => {
                this.auditTrailService.getCardAuditTrail(this.currentDashboard.Id, this.cardId).subscribe(async (data) => {
                    this.isLoading = false;
                    this.auditsData = data.Audits;
                    this.gridMainData = this.updateViewModel(this.auditsData);
                    if (this.gridMainData !== undefined && this.gridMainData.length > 0) {
                        this.gridData = this.gridMainData;
                        let actioncount = 1;
                        let userIDcount = 1;
                        this.gridData.forEach((element: any) => {
                            if (this.actionData.Option.findIndex((x) => x.value == element.action) === -1) {
                                this.actionData.Option.push({ id: actioncount++, value: element.action });
                            }
                            if (this.userIDData.Option.findIndex((x) => x.value == element.user) === -1) {
                                this.userIDData.Option.push({ id: userIDcount++, value: element.user });
                            }
                        });
                        this.loadItems();
                        span?.setAttribute("Firm name", (await this.firmsService.getFirmById(this.authService.currentUser.firm_id)).Name);
                        span?.setAttribute("Tax year", this.currentDashboard?.TaxYear);
                        span?.end();
                    }
                });
            },
        );
    }

    retrieveEntityTypeFromAction(action: string): string {
        let entityType = "";

        const possibleActions = ["card", "category", "role", "step", "user-dashboard-setting", "user"];

        entityType = possibleActions.find((possibleAction) => {
            return action.startsWith(possibleAction);
        });

        return entityType;
    }

    retrievePrettyName(entityType: string, entityId: string): string {
        let prettyName = entityId;

        switch (entityType) {
            case "card": {
                const card = this.cardDisplayService.getCardById(entityId);
                if (card !== undefined) {
                    prettyName = card.name;
                }
                break;
            }
            case "category": {
                const category = this.categories.find((c) => c.Id === entityId);
                if (category) {
                    prettyName = category.Name[this.currentLanguage];
                }
                break;
            }
            case "role": {
                const role = this.iFirmRolesService.getRoleById(entityId);
                if (role) {
                    prettyName = role.Description[this.currentLanguage];
                }
                break;
            }
            case "step": {
                const steps = this.mappingOfStatusesService.Steps;
                const step = steps.find((s) => {
                    return s.Id === entityId;
                });
                if (step) {
                    prettyName = step.Name[this.currentLanguage];
                }
                break;
            }
            case "user-dashboard-setting": {
                const uds = this.userDashboardSettingsService.userDashboardSettingsById().get(entityId);
                if (uds) {
                    let udsUser: any = this.usersService.usersById()?.fromId.get(uds.UserId);
                    if (!udsUser) udsUser = this.usersService.usersById()?.fromUserId.get(uds.UserId);
                    if (udsUser) {
                        const udsDashboard = udsUser.UserDashboardSettings?.get(uds.DashboardId);
                        if (udsDashboard) {
                            const dashboard = this.dashboardsService.dashboardsById()?.get(uds.DashboardId);
                            if (dashboard) prettyName = udsUser.FullName + " / " + dashboard.Title;
                        }
                    }
                }
                break;
            }
            case "user": {
                let user: any = this.usersService.usersById()?.fromId.get(entityId);
                if (!user) user = this.usersService.usersById()?.fromUserId.get(entityId);
                if (!user) {
                    // Support PowerUsers in audit trail
                    user = this.usersService.getUserById(entityId, true);
                    if (user) {
                        user.FullName = user.FirstName + " " + user.LastName;
                    }
                }
                if (user) prettyName = user.FullName;
                break;
            }
        }

        return prettyName;
    }

    FromDatehandler(e: any) {
        this.FromDatefilter = { logic: "and", filters: [] };
        if (e) {
            this.FromDatefilter.filters.push({
                field: "timestamp",
                operator: "gte",
                value: this.datePipe.transform(e.toISOString(), "yyyy-MM-dd HH:mm"),
            });
        }
        this.UpdateGridFilter();
    }

    ToDatehandler(e: any) {
        this.ToDatefilter = { logic: "and", filters: [] };
        if (e) {
            const date = new Date(e.toISOString());
            date.setDate(date.getDate() + 1);
            this.ToDatefilter.filters.push({
                field: "timestamp",
                operator: "lte",
                value: this.datePipe.transform(date.toISOString(), "yyyy-MM-dd HH:mm"),
            });
        }
        this.UpdateGridFilter();
    }

    onChange(e: any) {
        const filter: CompositeFilterDescriptor = { logic: "or", filters: [] };
        filter.filters.push({ field: "action", operator: "contains", value: e });
        filter.filters.push({ field: "user", operator: "contains", value: e });
        filter.filters.push({ field: "entity", operator: "contains", value: e });
        filter.filters.push({ field: "timestamp", operator: "contains", value: e });
        if (this.gridMainData) {
            this.gridData = filterBy(this.gridMainData, filter);
            this.skip = 0;
            this.loadItems();
        }
    }

    public ActionValueChange() {
        this.Actionfilter = { logic: "or", filters: [] };
        this.actionData.selectedValues.forEach((e1: any) => {
            this.Actionfilter.filters.push({ field: "action", operator: "eq", value: e1 });
        });
        this.UpdateGridFilter();
    }

    public UserValueChange() {
        this.Userfilter = { logic: "or", filters: [] };
        this.userIDData.selectedValues.forEach((e1: any) => {
            this.Userfilter.filters.push({ field: "user", operator: "eq", value: e1 });
        });
        this.UpdateGridFilter();
    }

    UpdateGridFilter() {
        if (this.gridMainData) {
            const datefilter: CompositeFilterDescriptor = { logic: "and", filters: [] };
            datefilter.filters = this.ToDatefilter.filters.concat(this.FromDatefilter.filters);
            this.gridData = filterBy(this.gridMainData, this.Actionfilter);
            this.gridData = filterBy(this.gridData, this.Userfilter);
            this.gridData = filterBy(this.gridData, datefilter);
            this.skip = 0;
            this.loadItems();
        }
    }

    updateViewModel(events: AuditTrailEvent[]): AuditTrailEventViewModel[] {
        const viewModelEvents: AuditTrailEventViewModel[] = [];
        const dateFormat = this.currentLanguage === "en-CA" ? "yyyy-MM-dd hh:mm aa" : "yyyy-MM-dd HH:mm";
        if (events !== undefined && events.length > 0) {
            events.forEach((event) => {
                if (event.OldData !== "null") {
                    try {
                        if (!event || !event.OldData) return;

                        const eventData = JSON.parse(event.OldData) as PathObject[];
                        eventData.forEach((data1) => {
                            if (
                                !data1.path.startsWith("_", 1) &&
                                (data1.value ||
                                    data1.path.startsWith("/roleUserMapping/") ||
                                    data1.path === "/categoryId" ||
                                    [
                                        "/job/currentlyResponsible",
                                        "/job/manager",
                                        "/job/partner",
                                        "/job/owner",
                                        "/job/group1",
                                        "/job/group2",
                                        "/contactInformation/manager",
                                        "/contactInformation/partner",
                                    ].includes(data1.path))
                            ) {
                                let stepInfo: StepInfo | undefined = undefined;
                                if ((stepInfo = this.checkForStepChange(data1))) {
                                    viewModelEvents.push({
                                        user: this.retrievePrettyName("user", event.UserId),
                                        action: this.translateService.instant(stepInfo.stepPath),
                                        timestamp: this.datePipe.transform(event.OperationTime + "Z", dateFormat, undefined, "en-US") ?? "",
                                        entity: stepInfo.stepValue,
                                        operationTime: event.OperationTime,
                                    });
                                }

                                const func = this.prettyPathNames().get(data1.path.includes("roleUserMapping") ? "/roleUserMapping" : data1.path);
                                if (func) {
                                    func(data1, event.OperationTime, event.NewData);
                                    viewModelEvents.push({
                                        user: this.retrievePrettyName("user", event.UserId),
                                        action: this.translateService.instant(data1.path),
                                        timestamp: this.datePipe.transform(event.OperationTime + "Z", dateFormat, undefined, "en-US") ?? "",
                                        entity: data1.value,
                                        operationTime: event.OperationTime,
                                    });
                                }
                            }
                        });
                    } catch {
                        /* Error decoding URI component:*/
                    }
                } else {
                    viewModelEvents.push({
                        user: this.retrievePrettyName("user", event.UserId),
                        action: this.translateService.instant("AuditTrail.Events." + event.Action),
                        timestamp: this.datePipe.transform(event.OperationTime + "Z", dateFormat, undefined, "en-US") ?? "",
                        entity: this.retrievePrettyName(this.retrieveEntityTypeFromAction(event.Action), event.RowId),
                        operationTime: event.OperationTime,
                    });
                }
            });
        }

        return viewModelEvents.sort((a: any, b: any) => new Date(b.operationTime).getTime() - new Date(a.operationTime).getTime());
    }

    checkForStepChange(pathObj: PathObject): StepInfo | undefined {
        if (pathObj.path == "/return/statusId" || pathObj.path == "/job/statusId") {
            const step = this.mappingOfStatusesService.getStepFromStatusId(pathObj.value);
            const value = step ? (step.Name as any)[this.currentLanguage] : this.translateService.instant("AuditTrail.ActionOptions.StepDeleted");
            return {
                stepPath: this.translateService.instant("AuditTrail.ActionOptions.StepChanged"),
                stepValue: value,
            } as StepInfo;
        }
    }

    private group1Id: (pathObj: PathObject) => void = (pathObj) => {
        const title = this.iFirmGroupsService.getGroup1Caption(this.currentLanguage);
        pathObj.path = `${title} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setGroup1Value(pathObj, title);
    };

    private group2Id: (pathObj: PathObject) => void = (pathObj) => {
        const title = this.iFirmGroupsService.getGroup2Caption(this.currentLanguage);
        pathObj.path = `${title} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setGroup2Value(pathObj, title);
    };

    private quiet: (pathObj: PathObject, OperationTime: string) => void = (pathObj, OperationTime) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.SnoozeChanged");
        this.dateTimeAsNumber(pathObj);
    };

    private deliveryMethod: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.DeliveryMethodChanged");
        pathObj.value = this.deliveryMethods[pathObj.value] ? this.deliveryMethods[pathObj.value][this.currentLanguage] : null;
    };

    private categoryId: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.CategoryChanged");
        let isCategoryDeleted = true;
        if (!pathObj.value) {
            pathObj.value = this.translateService.instant("General.Unassigned");
        } else {
            const found = this.categories.find((c) => c.Id === pathObj.value);
            if (found) {
                isCategoryDeleted = false;
                pathObj.value = (found.Name as any)[this.currentLanguage];
            }
            if (isCategoryDeleted) {
                pathObj.value = this.translateService.instant("AuditTrail.ActionOptions.CategoryDeleted");
            }
        }
    };

    private documentsReceivedDate: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.DateReceivedChanged");
        this.dateTimeAsNumber(pathObj);
    };

    private dateNeeded: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.DateNeededChanged");
        this.dateTimeAsNumber(pathObj);
    };

    private note: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.PermanentNotesuChanged");
        pathObj.value = this.getNoteText(pathObj.value);
    };

    private temporaryNote: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.TemporaryNotesChanged");
        pathObj.value = this.getNoteText(pathObj.value);
    };

    private returnStatus: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.ReturnStatusChanged");
        let isStatusDeleted = true;
        this.mappingOfStatusesService.getAllReturnStatuses().forEach((status) => {
            if (status.TaxReturnStatus && status.TaxReturnStatus.Id == pathObj.value) {
                isStatusDeleted = false;
                pathObj.value = (status.TaxReturnStatus.Name as any)[this.currentLanguage];
            }
        });
        if (isStatusDeleted) {
            pathObj.value = this.translateService.instant("AuditTrail.ActionOptions.ReturnStatusDeleted");
        }
    };

    private roleUserMapping: (pathObj: PathObject, OperationTime: string, newValue: string) => void = (pathObj, _OperationTime, newValue) => {
        if (pathObj.op === "replace") {
            const card = JSON.parse(newValue);
            const path = pathObj.path.slice(17).split("/");
            const entry = parseInt(path[0], 10);
            const role = card.roleUserMapping[entry];
            let roleId = role.roleId;
            let userId = role.userId;
            if (path[1] === "userId") {
                userId = pathObj.value;
            } else {
                roleId = pathObj.value;
            }
            const userRole = this.iFirmRolesService.getRoleById(roleId);
            if (!userRole) {
                pathObj.path =
                    this.translateService.instant("AuditTrail.ActionOptions.RoleDeleted") +
                    " " +
                    this.translateService.instant("AuditTrail.ActionOptions.Changed");
            } else {
                pathObj.path =
                    (userRole.Description as any)[this.currentLanguage] + " " + this.translateService.instant("AuditTrail.ActionOptions.Changed");
            }
            if (isUnassigned(userId)) {
                pathObj.value = this.translateService.instant("General.None");
            } else {
                const user = this.usersService.getUserById(userId);
                if (user) {
                    pathObj.value = user.FirstName + " " + user.LastName;
                } else {
                    const iFirmUser = this.iFirmUsersService.Users.find((ifirmUser) => ifirmUser.UserId.toString() == userId);
                    if (iFirmUser) {
                        pathObj.value = iFirmUser.FirstName + " " + iFirmUser.LastName;
                    } else {
                        // Not a Tracking user, display the ID
                        pathObj.value = userId;
                    }
                }
            }
        } else {
            // Filter out mappping added by ClientInfo after a card creation
            if (pathObj.op === "add" && !pathObj.value.userId) {
                return false;
            }
            if (pathObj.op === "remove" && !pathObj.value) {
                return false;
            }
            if (pathObj.value.roleId !== undefined) {
                const userRole = this.iFirmRolesService.getRoleById(pathObj.value.roleId);
                if (!userRole) {
                    pathObj.path =
                        this.translateService.instant("AuditTrail.ActionOptions.RoleDeleted") +
                        " " +
                        this.translateService.instant("AuditTrail.ActionOptions.Changed");
                } else {
                    pathObj.path =
                        (userRole.Description as any)[this.currentLanguage] + " " + this.translateService.instant("AuditTrail.ActionOptions.Changed");
                }
                if (isUnassigned(pathObj.value.userId)) {
                    pathObj.value = this.translateService.instant("General.None");
                } else {
                    const user = this.usersService.getUserById(pathObj.value.userId);
                    if (user) {
                        pathObj.value = user.FirstName + " " + user.LastName;
                    } else {
                        // Not a Tracking user, display the ID
                        pathObj.value = pathObj.value.userId;
                    }
                }
            }
        }
    };

    private periodEndDate: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.PeriodEndDateChanged");
        this.dateTimeAsNumber(pathObj);
    };

    private targetStartDate: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.TargetStartDateChanged");
        this.dateTimeAsNumber(pathObj);
    };

    private targetEndDate: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.TargetEndDateChanged");
        this.dateTimeAsNumber(pathObj);
    };

    private jobStatus: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.JobStatusChanged");
        let isStatusDeleted = true;
        this.mappingOfStatusesService.getAllReturnStatuses().forEach((status) => {
            if (status.TaxReturnStatus && status.TaxReturnStatus.Id == pathObj.value) {
                isStatusDeleted = false;
                pathObj.value = (status.TaxReturnStatus.Name as any)[this.currentLanguage];
            }
        });
        if (isStatusDeleted) {
            pathObj.value = this.translateService.instant("AuditTrail.ActionOptions.JobStatusDeleted");
        }
    };

    private jobType: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.JobTypeChanged");
        pathObj.value = this.wkTranslatePipe.transform(this.jobsAndBillingJobTypesService.jobTypes().get(pathObj.value)?.name ?? "") ?? "";
        if (pathObj.value === "") pathObj.value = this.translateService.instant("AuditTrail.ActionOptions.JobTypeDeleted");
    };

    private priority: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = this.translateService.instant("AuditTrail.ActionOptions.PriorityChanged");
        const label = this.jobsAndBillingPriorityService.jobPriorityI18nKeys[pathObj.value];
        pathObj.value = this.translateService.instant(label);
    };

    private contactGroup1: (pathObj: PathObject) => void = (pathObj) => {
        const title = `${this.translateService.instant("JobsAndBilling.Contact.Title")} ${this.iFirmGroupsService.getGroup1Caption(this.currentLanguage)}`;
        pathObj.path = `${title} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setGroup1Value(pathObj, title);
    };

    private contactGroup2: (pathObj: PathObject) => void = (pathObj) => {
        const title = `${this.translateService.instant("JobsAndBilling.Contact.Title")} ${this.iFirmGroupsService.getGroup2Caption(this.currentLanguage)}`;
        pathObj.path = `${title} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setGroup2Value(pathObj, title);
    };

    private contactManager: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = `${this.translateService.instant("JobsAndBilling.Contact.Manager")} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setAssigneeValue(pathObj);
    };

    private contactPartner: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = `${this.translateService.instant("JobsAndBilling.Contact.Partner")} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setAssigneeValue(pathObj);
    };

    private jobCurrentlyResponsible: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = `${this.translateService.instant("JobsAndBilling.AssignedRoles.CurrentlyResponsible")} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setAssigneeValue(pathObj);
        if (!this.jobsAndBillingCommonService.currentSettings()?.currentlyResponsibleEnabled) {
            pathObj.value = `${pathObj.value} ${this.translateService.instant("AuditTrail.ActionOptions.RoleDeleted")}`;
        }
    };

    private jobManager: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = `${this.translateService.instant("JobsAndBilling.AssignedRoles.JobManager")} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setAssigneeValue(pathObj);
        if (!this.jobsAndBillingCommonService.currentSettings()?.jobManagerEnabled) {
            pathObj.value = `${pathObj.value} ${this.translateService.instant("AuditTrail.ActionOptions.RoleDeleted")}`;
        }
    };

    private jobPartner: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = `${this.translateService.instant("JobsAndBilling.AssignedRoles.JobPartner")} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setAssigneeValue(pathObj);
        if (!this.jobsAndBillingCommonService.currentSettings()?.jobPartnerEnabled) {
            pathObj.value = `${pathObj.value} ${this.translateService.instant("AuditTrail.ActionOptions.RoleDeleted")}`;
        }
    };

    private jobOwner: (pathObj: PathObject) => void = (pathObj) => {
        pathObj.path = `${this.translateService.instant("JobsAndBilling.AssignedRoles.JobOwner")} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setAssigneeValue(pathObj);
    };

    private jobGroup1: (pathObj: PathObject) => void = (pathObj) => {
        const title = `${this.translateService.instant("JobsAndBilling.Job")} ${this.iFirmGroupsService.getGroup1Caption(this.currentLanguage)}`;
        pathObj.path = `${title} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setGroup1Value(pathObj, title);
    };

    private jobGroup2: (pathObj: PathObject) => void = (pathObj) => {
        const title = `${this.translateService.instant("JobsAndBilling.Job")} ${this.iFirmGroupsService.getGroup2Caption(this.currentLanguage)}`;
        pathObj.path = `${title} ${this.translateService.instant("AuditTrail.ActionOptions.Changed")}`;
        this.setGroup2Value(pathObj, title);
    };

    private setGroup1Value(pathObj: PathObject, title: string): void {
        pathObj.value = pathObj.value
            ? (this.iFirmGroupsService.getGroup1OptionCaption(pathObj.value, this.currentLanguage) ??
              `(${pathObj.path} ${this.translateService.instant("AuditTrail.ActionOptions.Deleted")})`)
            : this.translateService.instant("General.None");
        if (pathObj.value === "") pathObj.value = `(${title} ${this.translateService.instant("AuditTrail.ActionOptions.Deleted")})`;
    }

    private setGroup2Value(pathObj: PathObject, title: string): void {
        pathObj.value = pathObj.value
            ? (this.iFirmGroupsService.getGroup2OptionCaption(pathObj.value, this.currentLanguage) ??
              `(${pathObj.path} ${this.translateService.instant("AuditTrail.ActionOptions.Deleted")})`)
            : this.translateService.instant("General.None");
        if (pathObj.value === "") pathObj.value = `(${title} ${this.translateService.instant("AuditTrail.ActionOptions.Deleted")})`;
    }

    private dateTimeAsNumber(pathObj: PathObject): void {
        pathObj.value = this.datePipe.transform(new Date(pathObj.value * 1000), "dd MMMM yyyy");
    }

    private setAssigneeValue(pathObj: PathObject): void {
        const getUserFromId = this.usersService.getUserFromId();
        pathObj.value = getUserFromId(pathObj.value)?.FullName ?? pathObj.value ?? this.translateService.instant("General.None");
    }

    private prettyPathNames = computed(() => {
        const map = new Map<string, (pathObj: PathObject, OperationTime: string, newValue: string) => void>();
        map.set("/group1Id", this.group1Id);
        map.set("/group2Id", this.group2Id);
        map.set("/quiet", this.quiet);
        map.set("/deliveryMethod", this.deliveryMethod);
        map.set("/categoryId", this.categoryId);
        map.set("/documentsReceivedDate", this.documentsReceivedDate);
        map.set("/dateNeeded", this.dateNeeded);
        map.set("/note", this.note);
        map.set("/temporaryNote", this.temporaryNote);
        map.set("/return/statusId", this.returnStatus);
        map.set("/roleUserMapping", this.roleUserMapping);
        map.set("/job/typeId", this.jobType);
        map.set("/job/periodEndDate", this.periodEndDate);
        map.set("/job/targetStartDate", this.targetStartDate);
        map.set("/job/targetEndDate", this.targetEndDate);
        map.set("/job/statusId", this.jobStatus);
        map.set("/job/priority", this.priority);
        map.set("/contactInformation/group1", this.contactGroup1);
        map.set("/contactInformation/group2", this.contactGroup2);
        map.set("/contactInformation/manager", this.contactManager);
        map.set("/contactInformation/partner", this.contactPartner);
        map.set("/job/currentlyResponsible", this.jobCurrentlyResponsible);
        map.set("/job/manager", this.jobManager);
        map.set("/job/partner", this.jobPartner);
        map.set("/job/owner", this.jobOwner);
        map.set("/job/group1", this.jobGroup1);
        map.set("/job/group2", this.jobGroup2);

        return map;
    });

    getNoteText(htmlString: string): string {
        let rnoteString = htmlString;
        const bodyTextRegex = /<body[^>]*>(.*?)<\/body>/s;
        // Match the regex and extract the body text
        const match = htmlString.match(bodyTextRegex);

        if (match && match.length > 1) {
            rnoteString = match[1].trim();
        }
        return rnoteString;
    }

    onClose(e) {
        this.close.emit(null);
        if (e) {
            e.stopPropagation();
        }
    }

    toggleSize() {
        const windowService = this.windowComponent["service"] as DragResizeService;
        this.isMaximized = this.dialogWidth == this.dialogMinWidth;
        this.dialogWidth = this.isMaximized ? windowService.windowViewPort.width : this.dialogMinWidth;
        this.centerWindow();
    }

    centerWindow() {
        const windowService = this.windowComponent["service"] as DragResizeService;
        if (!windowService) {
            return;
        }
        if (windowService.options.state === "maximized") {
            return;
        }
        const wnd = windowService.windowViewPort;
        const top = Math.max(0, (wnd.height - this.dialogMinHeight) / 2);
        const left = Math.max(0, (wnd.width - this.dialogWidth) / 2);
        windowService.options.position = "fixed";
        this.windowComponent.setOffset("top", top);
        this.windowComponent.setOffset("left", left);
    }

    public isItemSelected(value, filter) {
        return filter.selectedValues.includes(value);
    }

    showTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;
        if ((element.nodeName === "TD" || element.className === "k-column-title") && element.offsetWidth < element.scrollWidth) {
            this.tooltipDir.toggle(element);
        } else {
            this.tooltipDir.hide();
        }
    }

    public pageChange(event: PageChangeEvent): void {
        this.skip = event.skip;
        this.loadItems();
    }

    private loadItems(): void {
        this.gridView = {
            data: this.gridData.slice(this.skip, this.skip + this.pageSize),
            total: this.gridData.length,
        };
        this.pagerButtonCount = this.gridData.length > 9 ? 10 : 0;
        this.pagerInfo = this.gridData.length > 1;
        this.previousNext = this.gridData.length > 9;
    }

    public onKeyDown(pressedKey) {
        if (pressedKey.key === "Enter") {
            return false;
        }
    }
}
